import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { Link } from 'react-router-dom';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import {
  performanceToColor,
  timeSeriesNameToColor,
  formatPnlToMatchInvestment,
  formatRoRToMatchInvestment
} from 'utils/widget-helpers';

const useStyles = makeStyles(theme => ({
  cell: {
    padding: '10px',
    textDecoration: 'none',
    display: 'block',
    border: `0.5px solid ${theme.palette.white}`,
    '&:hover': {
      boxShadow:
        '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
      opacity: '0.9',
      paddingLeft: '11px',
      paddingRight: '11px',
      border: 'unset'
    }
  },
  title: {
    fontSize: '15px',
    lineHeight: '18px',
    letterSpacing: 0,
    maxHeight: '70px',
    overflowY: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical'
  },
  investment: {
    fontSize: '12px',
    lineHeight: '13px',
    fontWeight: '600',
    letterSpacing: 0
  }
}));
numeral.register('locale', 'myLocale', {
  delimiters: {
    thousands: ',',
    decimal: '.'
  },
  abbreviations: {
    thousand: 'M',
    million: 'MM',
    billion: 'b',
    trillion: 't'
  },
  currency: {
    symbol: '$'
  }
});
numeral.locale('myLocale');

const dollarSign = <span style={{ display: 'inline-block', float: 'left' }}>$</span>;
const investmentSuffix = <span style={{ width: '22px', display: 'inline-block' }}>INV</span>;
const pnlSuffix = <span style={{ width: '22px', display: 'inline-block' }}>PnL</span>;

const HeatmapCell = ({
  name,
  investment,
  value,
  timeSeriesId,
  timeSeriesSetName,
  tsrCount,
  periodPnl,
  date,
  userRole
}) => {
  const classes = useStyles();

  const isDateYear = date.length === 4;

  const { backgroundColorBasedOnSetName, textColorBasedOnSetName } = timeSeriesNameToColor(
    timeSeriesSetName
  );

  const cellProps = performanceToColor(value);
  const { backgroundColor, icon, textColor } = cellProps;
  const isInternalUser = userRole !== 'External User';

  return (
    <Box
      bgcolor={backgroundColorBasedOnSetName || backgroundColor}
      component={Link}
      to={`canned/${timeSeriesId}`}
      height="140px"
      width="140px"
      color={textColorBasedOnSetName || textColor}
      className={classes.cell}
    >
      <Grid
        container
        direction="column"
        alignItems="flex-start"
        justify="space-between"
        style={{ minHeight: '110px' }}
      >
        <Grid item style={{ height: '54px' }}>
          <Typography className={classes.title}>{name}</Typography>
        </Grid>
        <Grid
          item
          container
          direction="row"
          alignItems="center"
          justify="space-between"
          style={{ height: '36px', marginBottom: '5px', marginTop: '3px' }}
        >
          <Grid item xs="auto">
            {isInternalUser && <Typography
              className={classes.investment}
              color="inherit"
              style={{ textAlign: 'right' }}
            >
              {investment ? (
                <>
                  {dollarSign} {numeral(investment).format('0.0 a')} {investmentSuffix}
                </>
              ) : (
                ' '
              )}
            </Typography>}
            {isInternalUser && <Typography
              className={classes.investment}
              color="inherit"
              style={{ textAlign: 'right' }}
            >
              {periodPnl ? (
                <>
                  {dollarSign} {formatPnlToMatchInvestment(periodPnl, investment)} {pnlSuffix}
                </>
              ) : (
                ' '
              )}
            </Typography>}
            <Typography
              className={classes.investment}
              color="inherit"
              style={{ textAlign: 'right', whiteSpace: 'break-spaces' }}
            >
              {value ? (
               <>
               {!isInternalUser ? (
                 <>
                   {formatRoRToMatchInvestment(value, investment, true)} {' '}
                 </>
               ) : (
                 <>
                  {formatRoRToMatchInvestment(value, investment)}{' '}
                 </>
               )}
             </>
              ) : (
                ''
              )}
            </Typography>
          </Grid>
          {!!value && (
            <Grid item xs="auto">
              {icon}
            </Grid>
          )}
        </Grid>
        {(!!value || !!investment) && isDateYear && !!tsrCount && (
          <Grid item>
            <Typography variant="subtitle2">({tsrCount} months)</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

HeatmapCell.propTypes = {
  name: PropTypes.string.isRequired,
  investment: PropTypes.number,
  value: PropTypes.number,
  timeSeriesSetName: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  tsrCount: PropTypes.number.isRequired,
  periodPnl: PropTypes.number,
  timeSeriesId: PropTypes.number.isRequired,
  userRole: PropTypes.string
};

HeatmapCell.defaultProps = {
  value: null,
  investment: null,
  periodPnl: null,
  userRole: null
};

export default HeatmapCell;
